import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DataRequest, DataRequestStatus } from "../types/api";
import { STATUS_BADGE, requestType, requestStatusLabel, formatDate } from "./YourRequestsPage";

interface ProjectRequestsPanelProps {
  projectId: number;
  requests: DataRequest[];
  currentUserId?: number;
}

export const ProjectRequestsPanel: React.FC<ProjectRequestsPanelProps> = ({
  projectId,
  requests,
  currentUserId,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("projectRequestsPanel");

  const mine = requests
    .filter((r) => r.user_id === currentUserId)
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  const goToRequest = (id: number) => {
    navigate(`/requests/${id}?project=${projectId}`);
  };

  const goToNewRequest = () => {
    navigate("/requests/new", { state: { preselectedProjectId: projectId } });
  };

  return (
    <div className="prp-panel">
      <style>{`
        .prp-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .prp-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .prp-new-btn {
          background: var(--fill-primary);
          color: var(--on-primary);
          border: none;
          padding: 7px 14px;
          border-radius: var(--radius);
          font-size: 13px;
          font-family: inherit;
          font-weight: 500;
          cursor: pointer;
        }
        .prp-view-all {
          font-size: 12px;
          color: var(--text-secondary);
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          font-family: inherit;
          text-decoration: underline;
        }
        .prp-list {
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
        }
        .prp-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          cursor: pointer;
        }
        .prp-row:not(:last-child) {
          border-bottom: 0.5px solid var(--border);
        }
        .prp-row:hover {
          background: var(--surface-1);
        }
        .prp-row-text {
          flex: 1;
          min-width: 0;
        }
        .prp-row-title {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .prp-row-sub {
          font-size: 12px;
          color: var(--text-muted);
          margin: 2px 0 0;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .prp-type-pill {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: 10px;
          color: var(--text-muted);
          background: var(--surface-1);
          padding: 1px 6px;
          border-radius: 8px;
        }
        .prp-status-badge {
          font-size: 12px;
          font-weight: 500;
          padding: 3px 9px;
          border-radius: var(--radius);
          flex-shrink: 0;
        }
      `}</style>

      <div className="prp-header">
        <h3 className="prp-title">{t("title")}</h3>
        <button className="prp-new-btn" onClick={goToNewRequest}>
          {t("newRequest")}
        </button>
      </div>

      {mine.length === 0 ? (
        <div className="no-data">{t("empty")}</div>
      ) : (
        <>
          <div className="prp-list">
            {mine.map((req) => {
              const type = requestType(req);
              const badge =
                STATUS_BADGE[req.status as DataRequestStatus] ||
                STATUS_BADGE.pending;
              return (
                <div
                  key={req.id}
                  className="prp-row"
                  onClick={() => goToRequest(req.id)}
                >
                  <div className="prp-row-text">
                    <p className="prp-row-title">{req.title}</p>
                    <p className="prp-row-sub">
                      {formatDate(req.created_at)}
                      <span className="prp-type-pill">
                        {type === "data" ? t("type.data") : t("type.access")}
                      </span>
                    </p>
                  </div>
                  <span
                    className="prp-status-badge"
                    style={{ background: badge.bg, color: badge.text }}
                  >
                    {requestStatusLabel(t, req.status as DataRequestStatus)}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: "10px" }}>
            <button
              className="prp-view-all"
              onClick={() => navigate(`/requests?project=${projectId}`)}
            >
              {t("viewAll")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
